import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import PersonRemoveOutlined from '@mui/icons-material/PersonRemoveOutlined';
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import { useAuth } from '../context/AuthContext';
import { formatPhone, isValidEmail } from '../utils/formatters';
import API_BASE from '../config/api';

const TABS = ['Details', 'Contacts', 'Services'];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL',
  'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia',
  'Germany', 'France', 'Spain', 'Italy', 'Japan', 'Brazil', 'Mexico', 'Other',
];

const CONTACT_TYPES = [
  'Rep', 'Account Manager', 'Billing', 'Operations', 'Driver', 'Crew Chief', 'Other',
];

const SECTION_LABEL_STYLE = {
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 500,
  fontSize: '11px',
  color: '#565656',
  letterSpacing: '2px',
  textTransform: 'uppercase',
  marginBottom: '16px',
  marginTop: '0',
  paddingBottom: '8px',
  borderBottom: '1px solid #3a3a3a',
};

const INPUT_STYLE = (focused) => ({
  width: '100%',
  backgroundColor: 'transparent',
  border: 'none',
  borderBottom: `1px solid ${focused ? '#39FF14' : '#3a3a3a'}`,
  borderRadius: 0,
  padding: '8px 4px',
  color: '#F5F5F5',
  fontFamily: "'Roboto', sans-serif",
  fontWeight: 400,
  fontSize: '15px',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease',
});

function Badge({ label, colorStyle }) {
  return (
    <span
      style={{
        fontFamily: "'Roboto', sans-serif",
        fontWeight: 500,
        fontSize: '11px',
        padding: '4px 10px',
        borderRadius: '10px',
        ...colorStyle,
      }}
    >
      {label}
    </span>
  );
}

function SaveIndicator({ saved, error }) {
  if (saved) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#39FF14' }}>
        <CheckCircleOutlined style={{ fontSize: 14, color: '#39FF14' }} />
        <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '11px', color: '#39FF14' }}>Saved</span>
      </span>
    );
  }
  if (error) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#BF2634' }}>
        <ErrorOutlined style={{ fontSize: 14, color: '#BF2634' }} />
        <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '11px', color: '#BF2634' }}>Error</span>
      </span>
    );
  }
  return null;
}

function FieldLabel({ label, saved, error }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
      <span
        style={{
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 500,
          fontSize: '12px',
          color: '#565656',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {label}
      </span>
      <SaveIndicator saved={saved} error={error} />
    </div>
  );
}

function InlineField({ label, fieldName, value, onChange, onBlur, saved, error, fullWidth }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
      <FieldLabel label={label} saved={saved} error={error} />
      <input
        type="text"
        value={value || ''}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={(e) => { setFocused(false); onBlur(fieldName, e.target.value); }}
        style={INPUT_STYLE(focused)}
      />
    </div>
  );
}

function InlineSelect({ label, fieldName, value, options, onChange, onBlur, saved, error, fullWidth, placeholder = '—' }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
      <FieldLabel label={label} saved={saved} error={error} />
      <select
        value={value || ''}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={(e) => { setFocused(false); onBlur(fieldName, e.target.value); }}
        style={{
          ...INPUT_STYLE(focused),
          cursor: 'pointer',
          appearance: 'none',
          WebkitAppearance: 'none',
          color: value ? '#F5F5F5' : '#565656',
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23565656' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 4px center',
          paddingRight: '24px',
        }}
      >
        <option value="" style={{ backgroundColor: '#2a2a2a', color: '#565656' }}>{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt} style={{ backgroundColor: '#2a2a2a', color: '#F5F5F5' }}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

export default function CompanyDetail({ company, onUpdate, type }) {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('Details');
  const [formData, setFormData] = useState(null);
  const [savedFields, setSavedFields] = useState({});
  const [errorFields, setErrorFields] = useState({});
  const formDataRef = useRef(formData);

  // Contacts tab
  const [companyContacts, setCompanyContacts] = useState([]);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [availablePersonnel, setAvailablePersonnel] = useState([]);
  const contactPickerRef = useRef(null);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  // Two-step picker
  const [pendingContact, setPendingContact] = useState(null);
  const [selectedContactTypes, setSelectedContactTypes] = useState([]);
  const [isPrimaryContact, setIsPrimaryContact] = useState(false);
  // Quick create form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newContactForm, setNewContactForm] = useState({ first_name: '', last_name: '', primary_email: '', cell_phone: '', title: '' });
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [createContactError, setCreateContactError] = useState(null);
  const [inviteSuccessMessage, setInviteSuccessMessage] = useState(false);
  // Invite message
  const [inviteShown, setInviteShown] = useState(null);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (company) {
      setFormData({ ...company });
      setSavedFields({});
      setErrorFields({});
    }
  }, [company?._id]);

  useEffect(() => {
    if (!company) return;
    setCompanyContacts([]);
    setIsLoadingContacts(true);

    axios.get(`${API_BASE}/api/personnel`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAvailablePersonnel(res.data))
      .catch(() => {});

    if (company.contacts && company.contacts.length > 0) {
      Promise.all(
        company.contacts.map((c) =>
          axios.get(`${API_BASE}/api/personnel/${c.personnel_profile_id}`,
            { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => ({ profile: r.data, contact_types: c.contact_types || [], is_primary: c.is_primary || false }))
            .catch(() => null)
        )
      ).then((results) => {
        setCompanyContacts(results.filter(Boolean));
        setIsLoadingContacts(false);
      });
    } else {
      setIsLoadingContacts(false);
    }
  }, [company?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleBlur(fieldName, value) {
    if (!company) return;

    try {
      const res = await axios.put(
        `${API_BASE}/api/companies/${company._id}`,
        { [fieldName]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(res.data);
      setFormData(res.data);
      setSavedFields((prev) => ({ ...prev, [fieldName]: true }));
      setTimeout(() => setSavedFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; }), 2000);
    } catch {
      setErrorFields((prev) => ({ ...prev, [fieldName]: true }));
      setTimeout(() => setErrorFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; }), 3000);
    }
  }

  async function handleCheckboxChange(fieldName, value) {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    try {
      const res = await axios.put(
        `${API_BASE}/api/companies/${company._id}`,
        { [fieldName]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(res.data);
      setFormData(res.data);
      setSavedFields((prev) => ({ ...prev, [fieldName]: true }));
      setTimeout(() => setSavedFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; }), 2000);
    } catch {
      setErrorFields((prev) => ({ ...prev, [fieldName]: true }));
      setTimeout(() => setErrorFields((prev) => { const n = { ...prev }; delete n[fieldName]; return n; }), 3000);
    }
  }

  function setField(key, value) {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }

  async function handleConfirmAddContact() {
    if (!pendingContact) return;
    const newEntry = {
      personnel_profile_id: pendingContact._id,
      contact_types: selectedContactTypes,
      is_primary: isPrimaryContact,
    };
    const updatedContacts = [...(company.contacts || []), newEntry];
    const finalContacts = isPrimaryContact
      ? updatedContacts.map((c) =>
          c.personnel_profile_id?.toString() === pendingContact._id?.toString()
            ? { ...c, is_primary: true }
            : { ...c, is_primary: false }
        )
      : updatedContacts;
    try {
      const res = await axios.put(
        `${API_BASE}/api/companies/${company._id}`,
        { contacts: finalContacts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(res.data);
      setCompanyContacts((prev) => [
        ...prev.map((cc) => isPrimaryContact ? { ...cc, is_primary: false } : cc),
        { profile: pendingContact, contact_types: selectedContactTypes, is_primary: isPrimaryContact },
      ]);
      setShowContactPicker(false);
      setPendingContact(null);
      setSelectedContactTypes([]);
      setIsPrimaryContact(false);
      setContactSearch('');
    } catch { /* silent */ }
  }

  async function handleRemoveContact(profileId) {
    const updatedContacts = (company.contacts || []).filter(
      (c) => c.personnel_profile_id?.toString() !== profileId
    );
    try {
      const res = await axios.put(
        `${API_BASE}/api/companies/${company._id}`,
        { contacts: updatedContacts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(res.data);
      setCompanyContacts((prev) => prev.filter((cc) => cc.profile?._id?.toString() !== profileId));
    } catch { /* silent */ }
  }

  async function handleSetPrimary(profileId) {
    const updatedContacts = (company.contacts || []).map((c) => ({
      ...c,
      is_primary: c.personnel_profile_id?.toString() === profileId,
    }));
    try {
      const res = await axios.put(
        `${API_BASE}/api/companies/${company._id}`,
        { contacts: updatedContacts },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(res.data);
      setCompanyContacts((prev) => prev.map((cc) => ({
        ...cc,
        is_primary: cc.profile?._id?.toString() === profileId,
      })));
    } catch { /* silent */ }
  }

  function toggleContactType(type) {
    setSelectedContactTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  }

  async function handleCreateContact(inviteAsUser = false) {
    if (!newContactForm.first_name.trim() || !newContactForm.last_name.trim()) {
      setCreateContactError('First and last name are required');
      return;
    }
    setIsCreatingContact(true);
    setCreateContactError(null);
    try {
      const body = {
        first_name: newContactForm.first_name.trim(),
        last_name: newContactForm.last_name.trim(),
        primary_email: newContactForm.primary_email.trim(),
        cell_phone: newContactForm.cell_phone.trim(),
        title: newContactForm.title.trim(),
      };
      if (inviteAsUser) body.invite_pending = true;
      const res = await axios.post(`${API_BASE}/api/personnel`, body,
        { headers: { Authorization: `Bearer ${token}` } });
      const newProfile = res.data;
      setAvailablePersonnel((prev) => [...prev, newProfile]);
      setShowCreateForm(false);
      setNewContactForm({ first_name: '', last_name: '', primary_email: '', cell_phone: '', title: '' });
      if (inviteAsUser) {
        console.log('INVITE_PENDING', newProfile._id);
        setInviteSuccessMessage(true);
        setTimeout(() => {
          setInviteSuccessMessage(false);
          setPendingContact(newProfile);
        }, 3000);
      } else {
        setPendingContact(newProfile);
      }
    } catch {
      setCreateContactError('Failed to create contact');
    } finally {
      setIsCreatingContact(false);
    }
  }

  function showInviteMessage(profileId) {
    setInviteShown(profileId);
    setTimeout(() => setInviteShown(null), 2000);
  }

  if (!company || !formData) {
    return (
      <div
        style={{
          flex: 1,
          height: 'calc(100vh - 56px)',
          backgroundColor: '#222222',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
        }}
      >
        <BusinessOutlined style={{ fontSize: 64, color: '#3a3a3a' }} />
        <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '16px', color: '#565656' }}>
          Select a {type} to view details
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        height: 'calc(100vh - 56px)',
        overflowY: 'auto',
        backgroundColor: '#222222',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Detail header */}
      <div
        style={{
          padding: '24px 32px 0 32px',
          borderBottom: '1px solid #3a3a3a',
          backgroundColor: '#2a2a2a',
        }}
      >
        {/* Name + badges */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}
        >
          <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '24px', color: '#FFFFFF' }}>
            {company.name}
          </span>
          <div style={{ display: 'flex', gap: '6px' }}>
            {company.is_client && (
              <Badge label="Client" colorStyle={{ backgroundColor: 'rgba(83,145,255,0.15)', color: '#5391FF' }} />
            )}
            {company.is_vendor && (
              <Badge label="Vendor" colorStyle={{ backgroundColor: 'rgba(191,38,52,0.15)', color: '#BF2634' }} />
            )}
            {company.is_promoter && (
              <Badge label="Promoter" colorStyle={{ backgroundColor: 'rgba(229,192,75,0.15)', color: '#E5C04B' }} />
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0 }}>
          {TABS.map((tab) => (
            <div
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 24px',
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: '14px',
                color: activeTab === tab ? '#39FF14' : '#888888',
                cursor: 'pointer',
                borderBottom: activeTab === tab ? '2px solid #39FF14' : '2px solid transparent',
                transition: 'all 0.15s ease',
              }}
            >
              {tab}
            </div>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ padding: '32px' }}>
        {activeTab === 'Details' && (
          <>
            {/* Primary fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              <InlineField
                label="Office Phone"
                fieldName="office_phone"
                value={formData.office_phone}
                onChange={(e) => setField('office_phone', formatPhone(e.target.value))}
                onBlur={handleBlur}
                saved={!!savedFields.office_phone}
                error={!!errorFields.office_phone}
              />
              <InlineField
                label="Website"
                fieldName="website"
                value={formData.website}
                onChange={(e) => setField('website', e.target.value)}
                onBlur={handleBlur}
                saved={!!savedFields.website}
                error={!!errorFields.website}
              />
              <InlineField
                label="Address"
                fieldName="address1"
                value={formData.address1}
                onChange={(e) => setField('address1', e.target.value)}
                onBlur={handleBlur}
                saved={!!savedFields.address1}
                error={!!errorFields.address1}
              />
              <InlineField
                label="City"
                fieldName="city"
                value={formData.city}
                onChange={(e) => setField('city', e.target.value)}
                onBlur={handleBlur}
                saved={!!savedFields.city}
                error={!!errorFields.city}
              />
              <InlineSelect
                label="State"
                fieldName="state"
                value={formData.state}
                options={US_STATES}
                onChange={(e) => setField('state', e.target.value)}
                onBlur={handleBlur}
                saved={!!savedFields.state}
                error={!!errorFields.state}
                placeholder="— State —"
              />
              <InlineField
                label="Zip"
                fieldName="zip"
                value={formData.zip}
                onChange={(e) => setField('zip', e.target.value)}
                onBlur={handleBlur}
                saved={!!savedFields.zip}
                error={!!errorFields.zip}
              />
              <InlineSelect
                label="Country"
                fieldName="country"
                value={formData.country}
                options={COUNTRIES}
                onChange={(e) => setField('country', e.target.value)}
                onBlur={handleBlur}
                saved={!!savedFields.country}
                error={!!errorFields.country}
                placeholder="— Country —"
              />
            </div>

            {/* ADDITIONAL section */}
            <div style={SECTION_LABEL_STYLE}>ADDITIONAL</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', marginBottom: '32px' }}>
              <InlineField
                label="Extension"
                fieldName="office_ext"
                value={formData.office_ext}
                onChange={(e) => setField('office_ext', e.target.value)}
                onBlur={handleBlur}
                saved={!!savedFields.office_ext}
                error={!!errorFields.office_ext}
              />
              <InlineField
                label="EIN"
                fieldName="ein"
                value={formData.ein}
                onChange={(e) => setField('ein', e.target.value)}
                onBlur={handleBlur}
                saved={!!savedFields.ein}
                error={!!errorFields.ein}
              />
              <InlineField
                label="W9 File"
                fieldName="w9_file"
                value={formData.w9_file}
                onChange={(e) => setField('w9_file', e.target.value)}
                onBlur={handleBlur}
                saved={!!savedFields.w9_file}
                error={!!errorFields.w9_file}
              />
              <InlineField
                label="Billing Address"
                fieldName="billing_address"
                value={formData.billing_address}
                onChange={(e) => setField('billing_address', e.target.value)}
                onBlur={handleBlur}
                saved={!!savedFields.billing_address}
                error={!!errorFields.billing_address}
                fullWidth
              />
            </div>

            {/* TYPE section */}
            <div style={SECTION_LABEL_STYLE}>TYPE</div>
            <div style={{ display: 'flex', gap: '24px' }}>
              {[
                { label: 'Client', key: 'is_client' },
                { label: 'Vendor', key: 'is_vendor' },
                { label: 'Promoter', key: 'is_promoter' },
              ].map(({ label, key }) => (
                <label
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: "'Roboto', sans-serif",
                    fontWeight: 400,
                    fontSize: '14px',
                    color: '#F5F5F5',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!formData[key]}
                    onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                    style={{ accentColor: '#39FF14', width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </>
        )}

        {activeTab === 'Contacts' && (
          <div>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', color: '#565656', letterSpacing: '2px', textTransform: 'uppercase' }}>
                CONTACTS
              </span>
              <button
                onClick={() => { setShowContactPicker(true); setPendingContact(null); setSelectedContactTypes([]); setIsPrimaryContact(false); setContactSearch(''); }}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: '1px solid #39FF14', color: '#39FF14', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}
              >
                + Contact
              </button>
            </div>

            {/* Loading */}
            {isLoadingContacts && (
              <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#888888', textAlign: 'center', padding: '32px 0' }}>
                Loading contacts...
              </div>
            )}

            {/* Empty state */}
            {!isLoadingContacts && companyContacts.length === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: '10px' }}>
                <PersonOutlined style={{ fontSize: 48, color: '#3a3a3a' }} />
                <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '14px', color: '#565656' }}>
                  No contacts added yet
                </div>
                <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '13px', color: '#3a3a3a', textAlign: 'center' }}>
                  Add Personnel profiles as contacts for this company
                </div>
              </div>
            )}

            {/* Contact cards */}
            {!isLoadingContacts && companyContacts.map((cc, idx) => {
              const p = cc.profile;
              const initials = p?.first_name ? p.first_name[0].toUpperCase() : '?';
              return (
                <div key={idx} style={{ backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '16px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {/* Avatar */}
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#565656', border: `2px solid ${cc.is_primary ? '#39FF14' : '#3a3a3a'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '18px', color: '#F5F5F5' }}>
                    {initials}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Name + primary badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                      <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '15px', color: '#F5F5F5' }}>
                        {[p?.first_name, p?.last_name].filter(Boolean).join(' ') || '—'}
                      </span>
                      {cc.is_primary && (
                        <span style={{ backgroundColor: 'rgba(57,255,20,0.1)', color: '#39FF14', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '10px', padding: '2px 8px', borderRadius: '10px' }}>
                          Primary
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    {p?.title && (
                      <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#888888', marginBottom: '4px' }}>
                        {p.title}
                      </div>
                    )}

                    {/* Contact type tags */}
                    {cc.contact_types?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px', marginBottom: '4px' }}>
                        {cc.contact_types.map((t) => (
                          <span key={t} style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: '#888888', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', padding: '2px 8px', borderRadius: '10px' }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Contact info */}
                    <div style={{ display: 'flex', gap: '16px', marginTop: '4px' }}>
                      {p?.cell_phone && (
                        <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656' }}>{p.cell_phone}</span>
                      )}
                      {p?.primary_email && (
                        <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#565656' }}>{p.primary_email}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                    {!cc.is_primary && (
                      <button
                        onClick={() => handleSetPrimary(p?._id?.toString())}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#39FF14'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#565656'; }}
                        style={{ background: 'transparent', border: 'none', color: '#565656', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', cursor: 'pointer', padding: '0' }}
                      >
                        Set Primary
                      </button>
                    )}
                    {/* Invite / linked status */}
                    {(cc.profile?.is_linked || cc.profile?.user_id) ? (
                      <span style={{ backgroundColor: 'rgba(57,255,20,0.08)', color: '#39FF14', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '10px', padding: '2px 8px', borderRadius: '10px' }}>
                        ✓ Account Linked
                      </span>
                    ) : inviteShown === p?._id?.toString() ? (
                      <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontStyle: 'italic', fontSize: '11px', color: '#E5C04B' }}>
                        Coming soon
                      </span>
                    ) : (
                      <button
                        onClick={() => { console.log('invite', p?._id); showInviteMessage(p?._id?.toString()); }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#E5C04B'; e.currentTarget.style.color = '#E5C04B'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#565656'; e.currentTarget.style.color = '#565656'; }}
                        style={{ background: 'transparent', border: '1px solid #565656', color: '#565656', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '11px', padding: '3px 10px', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Invite as User
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveContact(p?._id?.toString())}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#BF2634'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#565656'; }}
                      style={{ background: 'none', border: 'none', color: '#565656', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}
                    >
                      <PersonRemoveOutlined style={{ fontSize: 20 }} />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Contact picker modal */}
            {showContactPicker && (
              <div
                onClick={(e) => { if (e.target === e.currentTarget) { setShowContactPicker(false); setPendingContact(null); setShowCreateForm(false); setCreateContactError(null); } }}
                style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
              >
                <div ref={contactPickerRef} style={{ width: '420px', backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', boxShadow: '0 16px 48px rgba(0,0,0,0.6)', overflow: 'hidden' }}>
                  {/* Panel header */}
                  <div style={{ backgroundColor: '#222222', padding: '16px 20px', borderBottom: '1px solid #3a3a3a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {showCreateForm && (
                        <button
                          onClick={() => { setShowCreateForm(false); setCreateContactError(null); }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#F5F5F5'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = '#565656'; }}
                          style={{ background: 'transparent', border: 'none', color: '#565656', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}
                        >
                          <ArrowBackOutlined style={{ fontSize: 20 }} />
                        </button>
                      )}
                      <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '16px', color: '#FFFFFF' }}>
                        {showCreateForm ? 'New Contact' : pendingContact ? `Contact types for ${pendingContact.first_name || ''}` : 'Add Contact'}
                      </span>
                    </div>
                    <button
                      onClick={() => { setShowContactPicker(false); setPendingContact(null); setSelectedContactTypes([]); setIsPrimaryContact(false); setShowCreateForm(false); setCreateContactError(null); setNewContactForm({ first_name: '', last_name: '', primary_email: '', cell_phone: '', title: '' }); }}
                      style={{ background: 'transparent', border: 'none', color: '#565656', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: '0' }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Step 1: Person search or create form */}
                  {!pendingContact && !showCreateForm && (
                    <>
                      {inviteSuccessMessage && (
                        <div style={{ backgroundColor: 'rgba(229,192,75,0.1)', borderLeft: '3px solid #E5C04B', color: '#E5C04B', fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '13px', padding: '10px 16px', borderRadius: '4px', margin: '12px 20px' }}>
                          Contact created. Invite email coming soon.
                        </div>
                      )}
                      <input
                        value={contactSearch}
                        onChange={(e) => setContactSearch(e.target.value)}
                        autoFocus
                        placeholder="Search personnel..."
                        style={{ display: 'block', width: '100%', padding: '12px 20px', backgroundColor: '#1a1a1a', border: 'none', borderBottom: '1px solid #3a3a3a', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                      />
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {(() => {
                          const alreadyAdded = new Set(companyContacts.map((cc) => cc.profile?._id?.toString()));
                          const filtered = availablePersonnel.filter((p) => {
                            if (alreadyAdded.has(p._id?.toString())) return false;
                            const name = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
                            return name.includes(contactSearch.toLowerCase());
                          });
                          return (
                            <>
                              {filtered.length === 0 ? (
                                <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '13px', color: '#565656', padding: '20px' }}>
                                  No personnel found
                                </div>
                              ) : filtered.map((p) => (
                                <div
                                  key={p._id}
                                  onClick={() => { setPendingContact(p); setContactSearch(''); }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(57,255,20,0.06)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', cursor: 'pointer' }}
                                >
                                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#565656', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '14px', color: '#F5F5F5' }}>
                                    {p.first_name ? p.first_name[0].toUpperCase() : '?'}
                                  </div>
                                  <div>
                                    <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px', color: '#F5F5F5' }}>
                                      {[p.first_name, p.last_name].filter(Boolean).join(' ') || '—'}
                                    </div>
                                    <div style={{ fontFamily: "'Roboto', sans-serif", fontSize: '12px', color: '#565656' }}>
                                      {[p.title, p.cell_phone].filter(Boolean).join(' · ')}
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {/* Create new option */}
                              <div style={{ borderTop: '1px solid #3a3a3a', margin: '4px 0' }} />
                              <div
                                onClick={() => { setShowCreateForm(true); setCreateContactError(null); setNewContactForm({ first_name: '', last_name: '', primary_email: '', cell_phone: '', title: '' }); }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(57,255,20,0.06)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', cursor: 'pointer', color: '#39FF14' }}
                              >
                                <PersonAddOutlined style={{ fontSize: 16 }} />
                                <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px' }}>
                                  + Create New Contact
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </>
                  )}

                  {/* Quick create form */}
                  {!pendingContact && showCreateForm && (
                    <>
                    <div style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                        <div>
                          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', color: '#565656', marginBottom: '4px' }}>FIRST NAME</div>
                          <input
                            autoFocus
                            value={newContactForm.first_name}
                            onChange={(e) => setNewContactForm((p) => ({ ...p, first_name: e.target.value }))}
                            style={{ width: '100%', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', padding: '10px 14px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }}
                            onFocus={(e) => { e.target.style.borderColor = '#39FF14'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#3a3a3a'; }}
                          />
                        </div>
                        <div>
                          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', color: '#565656', marginBottom: '4px' }}>LAST NAME</div>
                          <input
                            value={newContactForm.last_name}
                            onChange={(e) => setNewContactForm((p) => ({ ...p, last_name: e.target.value }))}
                            style={{ width: '100%', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', padding: '10px 14px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }}
                            onFocus={(e) => { e.target.style.borderColor = '#39FF14'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#3a3a3a'; }}
                          />
                        </div>
                      </div>
                      <div>
                        <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', color: '#565656', marginBottom: '4px' }}>EMAIL</div>
                        <input
                          value={newContactForm.primary_email}
                          onChange={(e) => setNewContactForm((p) => ({ ...p, primary_email: e.target.value }))}
                          onBlur={(e) => { e.target.style.borderColor = newContactForm.primary_email && !isValidEmail(newContactForm.primary_email) ? '#BF2634' : '#3a3a3a'; }}
                          style={{ width: '100%', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', padding: '10px 14px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }}
                          onFocus={(e) => { e.target.style.borderColor = '#39FF14'; }}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                        <div>
                          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', color: '#565656', marginBottom: '4px' }}>CELL PHONE</div>
                          <input
                            value={newContactForm.cell_phone}
                            onChange={(e) => setNewContactForm((p) => ({ ...p, cell_phone: formatPhone(e.target.value) }))}
                            style={{ width: '100%', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', padding: '10px 14px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }}
                            onFocus={(e) => { e.target.style.borderColor = '#39FF14'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#3a3a3a'; }}
                          />
                        </div>
                        <div>
                          <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', color: '#565656', marginBottom: '4px' }}>TITLE</div>
                          <input
                            value={newContactForm.title}
                            onChange={(e) => setNewContactForm((p) => ({ ...p, title: e.target.value }))}
                            style={{ width: '100%', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '4px', padding: '10px 14px', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }}
                            onFocus={(e) => { e.target.style.borderColor = '#39FF14'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#3a3a3a'; }}
                          />
                        </div>
                      </div>
                      {createContactError && (
                        <div style={{ color: '#BF2634', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontStyle: 'italic', fontSize: '13px', marginBottom: '10px' }}>
                          {createContactError}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '0 20px 20px 20px' }}>
                      <button
                        onClick={() => handleCreateContact(false)}
                        disabled={isCreatingContact}
                        onMouseEnter={(e) => { if (!isCreatingContact) e.currentTarget.style.borderColor = '#F5F5F5'; }}
                        onMouseLeave={(e) => { if (!isCreatingContact) e.currentTarget.style.borderColor = '#565656'; }}
                        style={{ backgroundColor: '#2a2a2a', border: '1px solid #565656', color: '#F5F5F5', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px', padding: '10px 20px', borderRadius: '4px', cursor: isCreatingContact ? 'default' : 'pointer', opacity: isCreatingContact ? 0.7 : 1 }}
                      >
                        {isCreatingContact ? 'Creating...' : 'Create Contact'}
                      </button>
                      <button
                        onClick={() => handleCreateContact(true)}
                        disabled={isCreatingContact}
                        onMouseEnter={(e) => { if (!isCreatingContact) e.currentTarget.style.backgroundColor = '#8B1C26'; }}
                        onMouseLeave={(e) => { if (!isCreatingContact) e.currentTarget.style.backgroundColor = '#BF2634'; }}
                        style={{ backgroundColor: '#BF2634', color: '#FFFFFF', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px', padding: '10px 20px', borderRadius: '4px', border: 'none', cursor: isCreatingContact ? 'default' : 'pointer', opacity: isCreatingContact ? 0.7 : 1 }}
                      >
                        {isCreatingContact ? 'Creating...' : 'Invite as User'}
                      </button>
                    </div>
                    </>
                  )}

                  {/* Step 2: Contact types + primary */}
                  {pendingContact && (
                    <>
                      <div style={{ padding: '12px 20px', borderBottom: '1px solid #3a3a3a' }}>
                        <div style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px', color: '#F5F5F5', marginBottom: '12px' }}>
                          Select contact types for {[pendingContact.first_name, pendingContact.last_name].filter(Boolean).join(' ')}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {CONTACT_TYPES.map((type) => {
                            const active = selectedContactTypes.includes(type);
                            return (
                              <button
                                key={type}
                                onClick={() => toggleContactType(type)}
                                style={{ background: active ? 'rgba(57,255,20,0.1)' : '#2a2a2a', border: `1px solid ${active ? '#39FF14' : '#3a3a3a'}`, color: active ? '#39FF14' : '#888888', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '12px', padding: '6px 14px', borderRadius: '20px', cursor: 'pointer' }}
                              >
                                {type}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div style={{ padding: '12px 20px 16px 20px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: "'Roboto', sans-serif", fontSize: '14px', color: '#F5F5F5', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={isPrimaryContact}
                            onChange={(e) => setIsPrimaryContact(e.target.checked)}
                            style={{ accentColor: '#39FF14', width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          Set as primary contact
                        </label>
                      </div>
                      <div style={{ padding: '0 20px 20px 20px' }}>
                        <button
                          onClick={handleConfirmAddContact}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#8B1C26'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#BF2634'; }}
                          style={{ width: '100%', backgroundColor: '#BF2634', color: '#FFFFFF', fontFamily: "'Roboto', sans-serif", fontWeight: 500, fontSize: '14px', padding: '12px', borderRadius: '4px', border: 'none', cursor: 'pointer' }}
                        >
                          Add Contact
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'Services' && (
          <div>
            {!company.vendor_services || company.vendor_services.length === 0 ? (
              <span style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 300, fontSize: '14px', color: '#565656' }}>
                No services listed
              </span>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {company.vendor_services.map((s, i) => (
                  <span
                    key={i}
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      color: '#F5F5F5',
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 500,
                      fontSize: '13px',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      display: 'inline-flex',
                      margin: '4px',
                    }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
